/**
 * Petition page (templates/pages/petition.twig, modules/Petitions).
 *
 * The page is full-page cached, so:
 * - the counter and open/closed state are refreshed from the (short-cached)
 *   stats endpoint on page load;
 * - the sign form is posted with fetch() (reCAPTCHA v3 + honeypot, no CSRF)
 *   and the "check your mail" state is shown inline.
 */

import { getToken, loadRecaptcha } from './recaptcha.js';

const GENERIC_ERROR = 'Er ging iets mis bij het ondertekenen. Probeer het later opnieuw.';
const RECAPTCHA_ERROR = 'De beveiligingscontrole is mislukt. Controleer je internetverbinding en probeer het opnieuw.';

const numberFormat = new Intl.NumberFormat('nl-NL');

const petition = (config) => ({
  stats: config.stats,
  state: config.stats.open ? 'form' : 'closed', // 'form' | 'submitted' | 'closed'
  email: '',
  busy: false,
  message: '',
  errors: {},

  init() {
    this.refreshStats();
  },

  format(value) {
    return value === null || value === undefined ? '' : numberFormat.format(value);
  },

  async refreshStats() {
    if (!config.statsUrl) return;

    try {
      const response = await fetch(config.statsUrl, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;

      this.stats = await response.json();
      if (this.stats.closed && this.state === 'form') {
        this.state = 'closed';
      }
    } catch (e) {
      // Keep the server-rendered values
    }
  },

  preload() {
    if (config.recaptcha) {
      loadRecaptcha(config.recaptcha.siteKey).catch(() => {});
    }
  },

  async submit(event) {
    const form = event.target;
    if (this.busy) return;

    this.busy = true;
    this.message = '';
    this.errors = {};

    const body = new FormData(form);

    try {
      if (config.recaptcha) {
        body.set('g-recaptcha-response', await getToken(config.recaptcha.siteKey, config.recaptcha.action));
      }
    } catch (e) {
      this.message = RECAPTCHA_ERROR;
      this.busy = false;
      return;
    }

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body,
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        this.email = data.email || body.get('email');
        this.state = 'submitted';
        form.reset();
        this.$nextTick(() => this.$refs.submitted?.focus());
        return;
      }

      if (data.closed) {
        this.state = 'closed';
        this.refreshStats();
        return;
      }

      this.errors = data.errors || {};
      this.message = data.message || GENERIC_ERROR;
    } catch (e) {
      this.message = GENERIC_ERROR;
    } finally {
      this.busy = false;
    }
  },
});

/**
 * "Delen" dropdown. Instagram has no web share intent: use the native share
 * sheet on touch devices (which lists Instagram) and copy the link otherwise.
 */
const shareMenu = (config) => ({
  open: false,
  copied: false,

  toggle() {
    this.open = !this.open;
  },

  close() {
    this.open = false;
  },

  async copyLink() {
    if (navigator.share && window.matchMedia('(pointer: coarse)').matches) {
      try {
        await navigator.share({ title: config.title, url: config.url });
        this.close();
        return;
      } catch (e) {
        if (e.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(config.url);
      this.copied = true;
      window.setTimeout(() => { this.copied = false; }, 4000);
    } catch (e) {
      window.prompt('Kopieer de link naar de petitie:', config.url);
    }
  },
});

export default function registerPetition(Alpine) {
  Alpine.data('petition', petition);
  Alpine.data('shareMenu', shareMenu);
}
